import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, FileText, AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const TemplateSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('id');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [accessVerified, setAccessVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Função para verificar acesso
  const checkAccess = async () => {
    try {
      // Verificar se o usuário está autenticado
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setError('Sessão expirada. Por favor, faça login novamente');
        toast.error('Sessão expirada. Por favor, faça login novamente');
        setTimeout(() => navigate('/login'), 3000);
        return false;
      }

      // Verificar acesso ao template
      const { data, error: accessError } = await supabase
        .from('template_access')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('template_id', templateId)
        .single();

      if (accessError || !data) {
        console.log(`Tentativa ${verificationAttempts + 1}: Acesso ainda não verificado`);
        return false;
      } else {
        console.log('Acesso ao template verificado com sucesso!');
        return true;
      }
    } catch (error) {
      console.error('Erro ao verificar acesso ao template:', error);
      return false;
    }
  };

  useEffect(() => {
    if (!templateId) {
      setError('ID do template não fornecido');
      return;
    }

    let verificationTimer: NodeJS.Timeout;
    let redirectTimer: NodeJS.Timeout;

    const verifyAccess = async () => {
      // Verifica se o acesso já foi confirmado
      if (accessVerified) return;
      
      const hasAccess = await checkAccess();
      
      if (hasAccess) {
        // Acesso confirmado
        setAccessVerified(true);
        setLoading(false);
        toast.success('Acesso ao template confirmado!');
        
        // Aguarda 2 segundos antes de redirecionar
        redirectTimer = setTimeout(() => {
          navigate('/generate-invoice');
        }, 2000);
        
      } else {
        // Incrementa contagem de tentativas
        setVerificationAttempts(prev => prev + 1);
        
        if (verificationAttempts < 15) {  // Tenta por até 30 segundos (15 * 2s)
          // Tenta novamente após 2 segundos
          verificationTimer = setTimeout(verifyAccess, 2000);
        } else {
          // Após muitas tentativas, exibe erro mas ainda permite continuar
          setError('Não foi possível confirmar o acesso ao template. Tente acessar a página do gerador de faturas em alguns instantes.');
          setLoading(false);
        }
      }
    };

    verifyAccess();

    // Limpeza ao desmontar
    return () => {
      clearTimeout(verificationTimer);
      clearTimeout(redirectTimer);
    };
  }, [templateId, navigate, verificationAttempts, accessVerified]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Pagamento Confirmado!
          </h2>
          
          <p className="text-lg text-gray-600 mb-8">
            Seu pagamento foi processado com sucesso. {accessVerified ? 'O acesso ao template foi liberado!' : 'Estamos configurando seu acesso...'}
          </p>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <FileText className="h-6 w-6 text-blue-500" />
              <h3 className="text-xl font-semibold text-gray-900">Template {accessVerified ? 'Desbloqueado' : 'Em Processamento'}</h3>
            </div>
            
            {loading && (
              <div className="flex flex-col items-center justify-center py-4">
                <div className="animate-spin mb-4">
                  <Loader className="h-8 w-8 text-blue-500" />
                </div>
                <p className="text-gray-600">
                  {verificationAttempts > 0 
                    ? `Verificando acesso (tentativa ${verificationAttempts})...` 
                    : "Verificando seu acesso..."}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Isso pode levar alguns segundos.
                </p>
              </div>
            )}
            
            {accessVerified && (
              <div className="flex items-center justify-center space-x-2 text-green-600">
                <CheckCircle className="h-6 w-6" />
                <span className="font-medium">Acesso confirmado!</span>
              </div>
            )}
            
            {error && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                  <p className="text-sm text-yellow-700">{error}</p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <Link
              to="/generate-invoice"
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <span>Ir para Gerador de Faturas</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Próximos passos:</strong> Você agora tem acesso ao template premium. 
              Comece a criar suas faturas personalizadas!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSuccess; 